import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { LanguageToggle } from "@/components/LanguageToggle";
import { ThemeToggle } from "@/components/ThemeToggle";
import { AdminPanelButton } from "@/components/admin/AdminPanelButton";
import { LogoutConfirmDialog } from "@/components/LogoutConfirmDialog";
import { OrderFlowLogo } from "@/components/OrderFlowLogo";
import { LogOut, Maximize2, Minimize2, PanelLeft } from "lucide-react";
import { useSidebar } from "@/components/ui/sidebar";
import { useLanguage } from "@/contexts/LanguageContext";
import { NotificationCenter } from "@/components/navigation/NotificationCenter";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { useFullscreen } from "@/hooks/useFullscreen";
import { useIsMobile } from "@/hooks/use-mobile";
import { SIDEBAR } from "@/lib/uiConstants";

interface AppNavigationProps {
  /** Whether to show logout button (default: true) */
  showLogout?: boolean;
  /** Callback when logout is confirmed */
  onLogout?: () => Promise<void>;
  /** Sidebar width for grid alignment */
  sidebarWidth?: number;
  /** Additional CSS classes for the nav container */
  className?: string;
}

export function AppNavigation({
  showLogout = true,
  onLogout,
  sidebarWidth,
  className,
}: AppNavigationProps) {
  const { t } = useLanguage();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const { isFullscreen, toggleFullscreen, isSupported: fullscreenSupported } = useFullscreen();
  const isMobile = useIsMobile();
  const { toggleSidebar, open: isExpanded } = useSidebar();

  // Compute width for left column (sidebar area)
  const leftColumnWidth = sidebarWidth ?? (isExpanded ? SIDEBAR.expanded : SIDEBAR.collapsed);

  const handleSignOut = () => {
    setShowLogoutDialog(true);
  };

  const confirmSignOut = async () => {
    if (onLogout) {
      await onLogout();
    }
    setShowLogoutDialog(false);
  };

  return (
    <>
      <nav
        className={cn(
          "bg-gradient-to-r from-[hsl(var(--gradient-start))] to-[hsl(var(--gradient-end))] h-14",
          className
        )}
      >
        {/* 2-column grid: left column = sidebar width, right column = rest */}
        <div 
          className="h-full"
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : `${leftColumnWidth}px 1fr`,
            transition: `grid-template-columns ${SIDEBAR.animationMs}ms ease`,
          }}
        >
          {/* Left column: Toggle + Logo + (mobile: bell) */}
          <div className="flex items-center gap-2 px-3">
            {/* Toggle button - always visible */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleSidebar}
              aria-label="Toggle sidebar"
              className="h-8 w-8 shrink-0 text-white/80 hover:text-white hover:bg-white/10"
            >
              <PanelLeft className="h-4 w-4" />
            </Button>
            
            {/* Logo: show text when expanded (desktop) or always on mobile */}
            <OrderFlowLogo 
              size="small" 
              showText={isMobile || isExpanded} 
              animated={false} 
              to="/orders" 
              onGradientBackground 
            />

            {/* Mobile: bell pushed to far right */}
            {isMobile && (
              <div className="ml-auto">
                <NotificationCenter />
              </div>
            )}
          </div>

          {/* Right column: Controls (desktop only) */}
          {!isMobile && (
            <div className="flex items-center justify-end gap-0.5 sm:gap-1 px-3">
              <ThemeToggle onGradientBackground={true} />
              <LanguageToggle onGradientBackground={true} />
              <NotificationCenter />
              {fullscreenSupported && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost-utility"
                        size="icon"
                        onClick={toggleFullscreen}
                        className="h-8 w-8"
                      >
                        {isFullscreen ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="text-xs">
                      {isFullscreen ? t("fullscreen.exit") : t("fullscreen.enter")}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {showLogout && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleSignOut}
                        className="h-8 w-8 p-0 text-white/80 hover:text-white hover:bg-white/10"
                      >
                        <LogOut className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t("common.signOut")}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              <AdminPanelButton />
            </div>
          )}
        </div>
      </nav>

      <LogoutConfirmDialog
        open={showLogoutDialog}
        onOpenChange={setShowLogoutDialog}
        onConfirm={confirmSignOut}
      />
    </>
  );
}
